import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import {
  getRecordingUrl,
  getProctoringAnalysis,
  getSummaryData,
  getTranscriptData,
} from "../services/api";
import { useNotification } from "../../interviewAI/NotificationContext";
import ArrowBack from "@mui/icons-material/ArrowBack";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";

import ExecutiveInterviewReport from "./ExecutiveInterviewReport";

const InterviewVideoViewer = () => {
  const { interviewId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [videoData, setVideoData] = useState(null);
  const [proctoringData, setProctoringData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [transcriptData, setTranscriptData] = useState(null);
  const [activeEventIndex, setActiveEventIndex] = useState(null);

  const mainVideoRef = useRef(null);
  const secondaryVideoRef = useRef(null);

  const { notify } = useNotification();

  const handlePrint = () => {
    window.print();
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use Promise.allSettled to handle independent failures gracefully
        const results = await Promise.allSettled([
          getRecordingUrl(interviewId),
          getProctoringAnalysis(interviewId),
          getSummaryData(interviewId),
          getTranscriptData(interviewId),
        ]);

        const [videoRes, proctoringRes, summaryRes, transcriptRes] = results;

        setVideoData(videoRes.status === "fulfilled" ? videoRes.value : null);
        setProctoringData(
          proctoringRes.status === "fulfilled" ? proctoringRes.value : null,
        );
        setSummaryData(
          summaryRes.status === "fulfilled" ? summaryRes.value : null,
        );
        setTranscriptData(
          transcriptRes.status === "fulfilled" ? transcriptRes.value : null,
        );

        // Only show global error if ALL requests failed
        const allFailed = results.every((res) => res.status === "rejected");
        if (allFailed) {
          setError(
            "We couldn't load any data for this interview. It may have been deleted or is currently unavailable.",
          );
        }

        setLoading(false);
      } catch (err) {
        setError(err.message || "Failed to load data");
        setLoading(false);
        notify("error", err.message || "Failed to load data");
      }
    };

    if (interviewId) {
      fetchData();
    } else {
      setError("Invalid interview ID.");
      setLoading(false);
    }
  }, [interviewId, notify]);

  useEffect(() => {
    if (proctoringData?.candidate_name || summaryData?.candidate_name) {
      document.title = `Viriqo Hires Executive Report - ${proctoringData?.candidate_name || summaryData?.candidate_name}`;
    } else {
      document.title = "Viriqo Hires Executive Report";
    }
  }, [proctoringData, summaryData]);

  const parseTimestampToSeconds = (timestamp) => {
    if (!timestamp) return 0;
    const parts = timestamp.split(":").map(Number);
    if (parts.length === 3) {
      const [hours, minutes, seconds] = parts;
      return hours * 3600 + minutes * 60 + seconds;
    } else if (parts.length === 2) {
      const [minutes, seconds] = parts;
      return minutes * 60 + seconds;
    }
    return 0;
  };

  const formatDateTime = (isoString, timezone) => {
    if (!isoString) return "";

    try {
      const date = new Date(isoString);

      return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
        timeZone: timezone || "UTC",
      });
    } catch (e) {
      return "";
    }
  };

  const jumpToTimestamp = (timestamp, index) => {
    const seconds = parseTimestampToSeconds(timestamp);
    setActiveEventIndex(index);

    // Safely handle cases where video refs might not exist
    if (mainVideoRef.current) {
      mainVideoRef.current.currentTime = seconds;
      mainVideoRef.current
        .play()
        .catch((e) => console.log("Autoplay prevented:", e));
    }
    if (secondaryVideoRef.current) {
      secondaryVideoRef.current.currentTime = seconds;
      secondaryVideoRef.current
        .play()
        .catch((e) => console.log("Autoplay prevented:", e));
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#fafafa",
        }}
      >
        <CircularProgress size={40} thickness={3} sx={{ color: "#0f172a" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#fafafa",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 6,
            borderRadius: "16px",
            textAlign: "center",
            border: "1px solid #ececec",
          }}
        >
          <Typography
            sx={{
              fontSize: "1.1rem",
              fontWeight: 600,
              color: "#0f172a",
              mb: 1,
            }}
          >
            Couldn't load this recording
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: "#94a3b8", mb: 3.5 }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            component={Link}
            to="/interviews"
            startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
            sx={{
              borderRadius: "10px",
              fontWeight: 600,
              textTransform: "none",
              backgroundColor: "#0f172a",
              fontSize: "0.875rem",
              px: 2.5,
              py: 1,
              "&:hover": { backgroundColor: "#1e293b" },
            }}
          >
            Back to interviews
          </Button>
        </Paper>
      </Box>
    );
  }

  const infoItems = [
    {
      label: "Job opening",
      value: proctoringData?.job_opening_name,
      icon: <WorkOutlineRoundedIcon sx={{ fontSize: 17 }} />,
    },
    {
      label: "Candidate",
      value: proctoringData?.candidate_name,
      icon: <BadgeOutlinedIcon sx={{ fontSize: 17 }} />,
    },
    {
      label: "Date",
      value: proctoringData?.interview_scheduled_date,
      icon: <CalendarMonthRoundedIcon sx={{ fontSize: 17 }} />,
    },
    {
      label: "Time",
      value: proctoringData?.interview_scheduled_time,
      icon: <AccessTimeRoundedIcon sx={{ fontSize: 17 }} />,
    },
    {
      label: "Timezone",
      value: proctoringData?.interview_timezone,
      icon: <PublicRoundedIcon sx={{ fontSize: 17 }} />,
    },
    {
      label: "Started",
      value: formatDateTime(
        proctoringData?.interview_start_datetime,
        proctoringData?.interview_timezone,
      ),
      icon: <LoginRoundedIcon sx={{ fontSize: 17 }} />,
    },
    {
      label: "Ended",
      value: formatDateTime(
        proctoringData?.interview_end_datetime,
        proctoringData?.interview_timezone,
      ),
      icon: <LogoutRoundedIcon sx={{ fontSize: 17 }} />,
    },
  ];

  return (
    <ExecutiveInterviewReport
      interviewId={interviewId}
      videoData={videoData}
      proctoringData={proctoringData}
      summaryData={summaryData}
      transcriptData={transcriptData}
      infoItems={infoItems}
      mainVideoRef={mainVideoRef}
      secondaryVideoRef={secondaryVideoRef}
      activeEventIndex={activeEventIndex}
      jumpToTimestamp={jumpToTimestamp}
      parseTimestampToSeconds={parseTimestampToSeconds}
      formatDateTime={formatDateTime}
      onPrint={handlePrint}
    />
  );
};

export default InterviewVideoViewer;
